import React, { useMemo, useState, useCallback, useRef, useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Lazy, FreeMode, Navigation, Thumbs } from 'swiper';
import { Tooltip } from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { safeImageUrl } from '@utils/Utils';
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";
import { useIsMobile } from '@utils/hooks/useIsMobile';

import './style.scss';
import 'swiper/css';
import 'swiper/css/lazy';
import "swiper/css/effect-fade";
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

const ImageSlider = ({
    className = '',
    images = [],
    sizes = '100vw',
    withNavigation = false,
    loadPrevNext = false,
    thumbsPosition = "top",
    autoPlay = false,
    nested = false,
    slidesPerView = 1,
    spaceBetween = 0,
    variant = ''
}) => {
    const { REACT_APP_RESOURCE_DOMAIN } = useGlobalEnvConfig();
    const uid = useId();
    const tooltipId = `slide-counter-${uid}`;
    const tooltipSingleId = `slide-counter-single-${uid}`;
    const isMobile = useIsMobile();
    const thumbSpaceBetween = isMobile ? 8 : 16;
    const effectiveVariant = isMobile && variant === 'gallery-thumbs-right' ? 'gallery-no-thumbs' : variant;
    const showNavigation = withNavigation && images?.length > 1;
    const [loadedImages, setLoadedImages] = useState(new Set());
    const swiperRef = useRef();
    const thumbsSwiperRef = useRef(null);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [storedVariant, setStoredVariant] = useState(effectiveVariant);

    if (storedVariant !== effectiveVariant) {
        setStoredVariant(effectiveVariant);
        setThumbsSwiper(null);
    }

    const swiperKey = useMemo(() => `swiper-crop-${effectiveVariant}`, [effectiveVariant]);

    const handleImageLoad = useCallback((imageIndex) => {
        setLoadedImages(prev => new Set([...prev, imageIndex]));
    }, []);

    const onSwiper = useCallback((s) => {
        swiperRef.current = s;
    }, []);

    const handleSetThumbsSwiper = useCallback((swiper) => {
        thumbsSwiperRef.current = swiper;
        setThumbsSwiper(swiper);
    }, []);

    const handleSlideChange = useCallback((swiper) => {
        const newIndex = swiper.activeIndex;
        setActiveIndex(newIndex);
        setTimeout(() => {
            if (thumbsSwiperRef.current && !thumbsSwiperRef.current.destroyed) {
                thumbsSwiperRef.current.slideTo(newIndex);
            }
        }, 0);
    }, []);

    const getSwiper = useMemo(() => {
        const variantClass = effectiveVariant ? `variant-${effectiveVariant}` : '';
        return (
            <div key={swiperKey} className={`swiper-container ${slidesPerView > 1 ? 'mutiple-slides-per-view' : ''} ${variantClass}`}>
                <Swiper
                    autoplay={{
                        enabled: autoPlay && images.length > 1,
                        delay: 5000
                    }}
                    className={`slider ${className}`}
                    effect={slidesPerView > 1 ? "slide" : "fade"}
                    initialSlide={0}
                    lazy={{
                        checkInView: false,
                        enabled: false,
                        loadPrevNext: loadPrevNext,
                    }}
                    loop={false}
                    modules={[Autoplay, EffectFade, Lazy, FreeMode, Navigation, Thumbs]}
                    onSwiper={onSwiper}
                    onSlideChange={handleSlideChange}
                    navigation={showNavigation ? { prevEl: '.gallery-nav-prev', nextEl: '.gallery-nav-next' } : false}
                    thumbs={showNavigation ? { swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null } : undefined}
                    preloadImages={true}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    breakpoints={{
                        896: {
                            slidesPerView: slidesPerView > 1 ? 2.5 : 1,
                        },
                        1280: {
                            slidesPerView: slidesPerView
                        }
                    }}
                    speed={800}
                    runCallbacksOnInit={true}
                    nested={nested}
                >
                    {images?.map((image, i) => {
                        const imageUrl = safeImageUrl(REACT_APP_RESOURCE_DOMAIN, image.UrlFragment, image.Default.Width, image.Default.Height, image.Default.Mode, image.Default.QueryString);
                        const imageSrcSet = `${image.Alternates.map(x => `${safeImageUrl(REACT_APP_RESOURCE_DOMAIN, image.UrlFragment, x.Width, x.Height, x.Mode, x.QueryString)} ${x.Width}w`).join(', ')}, ${imageUrl} ${image.Default.Width}w`;
                        const isImageLoaded = loadedImages.has(i);

                        return (
                            <SwiperSlide key={`crop-${i}`}>
                                {image && (
                                    <img
                                        alt=""
                                        data-sizes={sizes}
                                        src={imageUrl}
                                        srcSet={imageSrcSet}
                                        onLoad={() => handleImageLoad(i)}
                                        style={{ opacity: isImageLoaded ? 1 : 0 }}
                                    />
                                )}

                                {!isImageLoaded && <div className="swiper-lazy-preloader"></div>}
                            </SwiperSlide>
                        );
                    })}
                    {showNavigation && (
                        <>
                            <button type="button" className="gallery-nav-prev swiper-navigation-button swiper-navigation-button-prev" aria-label="Previous slide">
                                <i><FontAwesomeIcon icon={faChevronLeft} /></i>
                            </button>
                            <button type="button" className="gallery-nav-next swiper-navigation-button swiper-navigation-button-next" aria-label="Next slide">
                                <i><FontAwesomeIcon icon={faChevronRight} /></i>
                            </button>
                        </>
                    )}
                </Swiper>
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length > 1 && (
                    <div className="slide-counter" data-tooltip-id={tooltipId}>
                        {images[activeIndex] && (images[activeIndex].imageLabel || images[activeIndex].imageDesc || images[activeIndex].imageCredit) && (
                            <span className="slide-counter-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        )}
                        {activeIndex + 1} of {images.length}
                    </div>
                )}
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length > 1 && images[activeIndex] && (images[activeIndex].imageLabel || images[activeIndex].imageDesc || images[activeIndex].imageCredit) && (
                    <Tooltip id={tooltipId} className="tooltip-content" place="left">
                        <div>
                            {images[activeIndex].imageLabel && (
                                <p className="mb-[8px] bold">
                                    {images[activeIndex].imageLabel}
                                </p>
                            )}
                            {images[activeIndex].imageDesc && (
                                <p className="mb-[8px]">{images[activeIndex].imageDesc}</p>
                            )}
                            {images[activeIndex].imageCredit && (
                                <p className="m-0">Credit: {images[activeIndex].imageCredit}</p>
                            )}
                        </div>
                    </Tooltip>
                )}
                {(effectiveVariant === 'gallery-thumbs-below' || effectiveVariant === 'gallery-no-thumbs' || effectiveVariant === 'gallery-thumbs-right') && images?.length === 1 && images[0] && (images[0].imageLabel || images[0].imageDesc || images[0].imageCredit) && (
                    <>
                        <div className="slide-counter icon-only" data-tooltip-id={tooltipSingleId}>
                            <span className="slide-counter-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </div>
                        <Tooltip id={tooltipSingleId} className="tooltip-content" place="left">
                            <div>
                                {images[0].imageLabel && <p className="mb-[8px] bold">{images[0].imageLabel}</p>}
                                {images[0].imageDesc && <p className="mb-[8px]">{images[0].imageDesc}</p>}
                                {images[0].imageCredit && <p className="m-0">Credit: {images[0].imageCredit}</p>}
                            </div>
                        </Tooltip>
                    </>
                )}
            </div>
        );
    }, [images, sizes, onSwiper, loadPrevNext, className, autoPlay, nested, slidesPerView, spaceBetween, REACT_APP_RESOURCE_DOMAIN, swiperKey, loadedImages, handleImageLoad, effectiveVariant, showNavigation, thumbsSwiper, activeIndex, handleSlideChange, tooltipId, tooltipSingleId]);

    // For medium variant (thumbs on right), wrap everything in a container
    if (effectiveVariant === 'gallery-thumbs-right' && showNavigation) {
        return (
            <div className="gallery-wrapper variant-gallery-thumbs-right">
                {getSwiper}
                <Swiper
                    onSwiper={handleSetThumbsSwiper}
                    loop={false}
                    spaceBetween={thumbSpaceBetween}
                    slidesPerView="auto"
                    freeMode={true}
                    watchSlidesProgress={true}
                    centeredSlides={false}
                    slideToClickedSlide={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className="swiper-thumbs"
                    direction="vertical"
                >
                    {images?.map((image, i) => {
                        const imageUrl = safeImageUrl(REACT_APP_RESOURCE_DOMAIN, image.UrlFragment, image.Alternates?.[0]?.Width || image.Default.Width, image.Alternates?.[0]?.Height || image.Default.Height, image.Alternates?.[0]?.Mode || image.Default.Mode, image.Alternates?.[0]?.QueryString || image.Default.QueryString);
                        return (
                            <SwiperSlide key={`thumb-${i}`}>
                                <img src={imageUrl} alt="" />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
        );
    }

    return (
        <>
            {getSwiper}
            {showNavigation && effectiveVariant !== 'gallery-no-thumbs' && (
                <Swiper
                    onSwiper={handleSetThumbsSwiper}
                    loop={false}
                    spaceBetween={thumbSpaceBetween}
                    slidesPerView="auto"
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className="swiper-thumbs"
                >
                    {images?.map((image, i) => {
                        const imageUrl = safeImageUrl(REACT_APP_RESOURCE_DOMAIN, image.UrlFragment, image.Alternates?.[0]?.Width || image.Default.Width, image.Alternates?.[0]?.Height || image.Default.Height, image.Alternates?.[0]?.Mode || image.Default.Mode, image.Alternates?.[0]?.QueryString || image.Default.QueryString);
                        return (
                            <SwiperSlide key={`thumb-${i}`}>
                                <img src={imageUrl} alt="" />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}
        </>
    );
};

export default ImageSlider; 